import { CrmTaskStatus, type Prisma } from '@plato/data/prisma';
import { plainTextToLexical } from '@plato/node-util/rich-text/plain-text-to-lexical';
import type { SerializedEditorState } from 'lexical';
import { z } from 'zod';
import { SortOrder } from '../types/list-view-types.ts';
import { crmAttachmentDtoSchema } from './crm-attachment-dto.ts';
import { platoUserDtoSchema } from './plato-user-dto.ts';

export const TaskSortBy = {
    TITLE: 'title',
    DUE_DATE: 'dueDate',
    COMPLETED_AT: 'completedAt',
    ASSIGNED_TO_USER: 'assignedTo',
    ASSIGNED_BY_USER: 'assignedBy',
} as const;

export type TaskSortBy = (typeof TaskSortBy)[keyof typeof TaskSortBy];

export const taskChangeStatusSchema = z.object({
    taskId: z.string(),
    status: z.enum(CrmTaskStatus),
});

export const taskDeleteSchema = z.object({
    taskId: z.string(),
});

export const taskIncludeParams = {
    assignedTo: true,
    assignedBy: true,
    createdBy: true,
    links: {
        where: {
            deletedAt: null,
        },
    },
    attachments: {
        where: {
            deletedAt: null,
        },
    },
} satisfies Prisma.CrmTaskInclude;

export const crmLinkModelType = z.enum(['customer', 'sales_order', 'sales_quote', 'ai_insights']);
export type CrmLinkModelType = z.infer<typeof crmLinkModelType>;

export const crmLinkDtoSchema = z
    .object({
        linkId: z.string(),
        modelType: z.string(),
        modelId: z.string(),
        title: z.string().nullable(),
        various: z.unknown().nullable(),
        deletedAt: z.date().nullable(),
        createdAt: z.date().nullable(),
        updatedAt: z.date().nullable(),
        createdById: z.string(),
    })
    .transform((link) => ({
        ...link,
        modelType: crmLinkModelType.parse(link.modelType),
    }));

export const crmTaskDtoSchema = z
    .object({
        taskId: z.uuid(),
        status: z.enum(CrmTaskStatus),
        description: z.string().nullable(),
        descriptionRich: z.unknown().nullable(),
        dueDate: z.date().nullable(),
        deletedAt: z.date().nullable(),
        createdAt: z.date().nullable(),
        updatedAt: z.date().nullable(),
        completedAt: z.date().nullable(),
        links: z.array(crmLinkDtoSchema),
        attachments: z.array(crmAttachmentDtoSchema),
        assignedTo: platoUserDtoSchema,
        assignedBy: platoUserDtoSchema,
        createdBy: platoUserDtoSchema,
    })
    .transform(async (data) => ({
        ...data,
        descriptionRich: (data.descriptionRich
            ? data.descriptionRich
            : await plainTextToLexical(data.description)) as SerializedEditorState,
    }));

export type CrmTaskDTO = z.infer<typeof crmTaskDtoSchema>;

export const crmTaskFilterSchema = z.object({
    status: z.enum(CrmTaskStatus).optional(),
    customerIds: z.array(z.string()).optional(),
    salesOrderIds: z.array(z.string()).optional(),
    salesQuoteIds: z.array(z.string()).optional(),
    assignedToIds: z.array(z.string()).optional(),
    assignedByIds: z.array(z.string()).optional(),
    userIds: z.array(z.string()).optional(),
    searchText: z.string().optional(),
    dueDate: z
        .array(
            z.object({
                direction: z.enum(['GTE', 'LT']),
                timestamp: z.string(),
            }),
        )
        .optional(),
    sortingFilter: z
        .object({
            field: z.enum(TaskSortBy),
            direction: z.enum(SortOrder),
        })
        .optional()
        .nullable(),
});

export type CrmTaskFilterOptions = z.infer<typeof crmTaskFilterSchema>;
